import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {State} from '@core/services/state';
import {CountryService} from '@core/services/country.service';
import {LeagueService} from '@core/services/league.service';
import {AppLeague} from '@shared/models/league';
import {Sex} from '@shared/models/sex';


interface Breadcrumb {
  leagueId: string,
  stateId: string,
  sex: Sex,
  image: string,
  name: string,
}

export enum GoBackValue {
  ToStart,
  ToState,
  ToSex,
}

@Component({
  selector: 'app-search-state-selected-component',
  templateUrl: './search-state-selected.component.html',
  styleUrls: ['./search-state-selected.component.scss']
})
export class SearchStateSelectedComponent implements OnChanges {
  @Input() onMobileView: boolean = false;
  @Input() stateId: string;
  @Input() selectedSex: Sex;
  @Input() leagueId: string;
  @Output() onSexSelected = new EventEmitter<Sex>();
  @Output() onLeagueSelected = new EventEmitter<string>();
  @Output() onFinishedLoading = new EventEmitter();
  @Output() onGoBack = new EventEmitter<{where: GoBackValue, id: string, sex: Sex}>();
  public breadcrumbs: Breadcrumb[] = [];
  public breadcrumbString: string = '';
  public sexes: Sex[] = [Sex.Male, Sex.Female, Sex.Youth];
  public state: State;
  public league: AppLeague;
  public name = '';
  public mainAndUpperLeagues: AppLeague[];
  public loading = false;

  constructor(
    private route: ActivatedRoute,
    private countryService: CountryService,
    private leagueService: LeagueService,
  ) { }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    this.breadcrumbs = [];
    this.league = null;
    this.state = null;
    this.name = '';
    this.mainAndUpperLeagues = [];
    this.loading = true;
    try {
      if (this.leagueId) {
        await this.getLeaguesForLeague(this.leagueId);
      } else if (this.selectedSex) {
        await this.loadState();
        await this.loadLeaguesOfState();
      } else if (this.stateId) {
        await this.loadState();
      }
      this.loading = false;
      this.onFinishedLoading.emit(true);
    } catch (e) {
      this.loading = false;
      this.onFinishedLoading.emit(true);
      throw e;
    }

  }

  private async loadState(): Promise<void> {
    this.state = await this.countryService.getState(this.stateId);
    this.name = this.state.name;
    this.addBreadCrumbForState(this.state);
  }

  private async loadLeaguesOfState(): Promise<void> {
    if (this.state == null || this.selectedSex == null) {
      return;
    }
    this.mainAndUpperLeagues = await this.leagueService.fetchStructuredLeaguesOfState(
      this.state.id, this.selectedSex.value
    );
  }

  private async getLeaguesForLeague(leagueId: string): Promise<void> {
    this.league = await this.leagueService.getLeague(leagueId);
    if (!this.league) {
      return;
    }
    this.name = this.league.name;
    if (this.league.upperleagueid) {
      const parentLeague = await this.leagueService.getLeague(this.league.upperleagueid);
      this.addBreadCrumbForLeague(parentLeague);
    } else {
      const state = await this.countryService.getState(this.league.getStateId());
      this.addBreadCrumbForState(state);
    }
    this.addBreadCrumbForLeague(this.league);
    this.mainAndUpperLeagues = await this.leagueService.fetchSubLeaguesOfLeague(this.league.id);
  }

  private addBreadCrumbForState(state: State) {
    this.breadcrumbs.push({
      leagueId: null,
      stateId: state.id,
      sex: null,
      image: state.getArmsImageUrl('medium'),
      name: state.name
    });
    this.breadcrumbString = `> ${state.name}`;

    if (this.selectedSex != null) {
      this.breadcrumbs.push({
        leagueId: null,
        stateId: state.id,
        sex: this.selectedSex,
        image: this.selectedSex.icon,
        name: this.selectedSex.germanString
      });
      this.breadcrumbString += ` > ${this.selectedSex.germanString}`;
    }
  }

  private addBreadCrumbForLeague(league: AppLeague) {
    this.breadcrumbs.push({
      leagueId: league.id,
      stateId: null,
      sex: null,
      image: league.getLogoImageUrl('medium'),
      name: league.name
    });
    if (league.name != null) {
      this.breadcrumbString += ` > ${league.name}`;
    }
  }

  public goBack(breadcrumb: Breadcrumb) {
    let stateId: string = null;

    if (breadcrumb == null) {
      this.onGoBack.emit({where: GoBackValue.ToStart, id: null, sex: null});
      return;
    }

    if (breadcrumb.leagueId != null) {
      // should never happen
    } else if (breadcrumb.sex != null) {
      stateId = breadcrumb.stateId;
      this.onGoBack.emit({where: GoBackValue.ToState, id: stateId, sex: breadcrumb.sex});
    } else if (breadcrumb.stateId != null) {
      stateId = breadcrumb.stateId;
      this.onGoBack.emit({where: GoBackValue.ToSex, id: stateId, sex: breadcrumb.sex});
    }
  }

  public sexClicked(sex: Sex) {
    this.onSexSelected.emit(sex);
  }

  public leagueClicked(league: AppLeague) {
    if (league.has_subleagues === true) {
      this.onLeagueSelected.emit(league.id);
    }
  }

}
